import {
  PrimaryButtonMedium,
  PrimaryButtonWithPlusIcon,
  SecondaryButtonMedium,
} from "../../../components/Buttons/Buttons";
import { SearchBar } from "../../../components/SearchBar/SearchBar";
import { Table } from "../../../components/Table/Table";
import { DropDown } from "../../../components/DropDown/DropDown";
import { Pagination } from "../../../components/Pagination/Pagination";
import {
  ContentWrapper,
  HorizontalButtonsContainer,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../layout/portalPageLayout";
import React, { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Modal } from "../../../components/Modal/Modal";
import { useHistory } from "react-router-dom";
import type {
  ITenantPaginatedOutputResponse,
  Tenant,
} from "../../../types/types";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useDebounce } from "../../../util/hooks";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import {
  formatAddressCityState,
  getRelativeTime,
  useStoreState,
  makeUrlWithParams,
  defaultHandleSort,
} from "../../../util/util";
import type { SortingRule } from "react-table";
import { useAuthContext } from "../../../components/Auth";
import { endpoints } from "../../../endpoints";
import { CreateNewTenantFormContainer } from "../../../components/CreateNewTenantForm/CreateNewTenantFormContainer";
import { CustomerDistributorDropdownContainer } from "../../../components/Layout/Layout";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { DownloadReport } from "../SellerOrdersListPage/DownloadReport/DownloadReport";
import type { ColumnDef } from "@tanstack/react-table";

type TenantsTable = {
  name: string;
  location: string;
  id: string;
};

const localStrings = (t: TFunction) => ({
  pageTitle: t("Customers"),
  searchBarPlaceholder: t("Search by company name"),
  externalID: t("Customer ID"),
  companyName: t("Company Name"),
  location: t("Location"),
  lastActivity: t("Last Activity"),
  status: t("Status"),
  createNew: t("Create New"),
  perPage: t("Per Page"),
});

/**
 * The "Customers" page that shows a table of customers.  Used for Seller Admin.
 */
export const CustomersListPage = () => {
  const user = useAuthenticatedUser();
  const { roleIsSomeKindOfSeller, roleIsAgilisAdmin } = useAuthContext();
  const { storefront_id, edition } = useStoreState();

  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(query?.perPage ?? perPageItems[0]);
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState<TenantsTable[]>([]);
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const closeModal = () => setShowModal(false);
  const openModal = () => setShowModal(true);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const [offset, setOffset] = useState(query.offset ?? 0);
  const [pagination, setPagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });

  const { t } = useTranslation();

  const [downloadReportSlideOut, setDownloadReportSlideOut] = useState(false);
  const closeDownloadReportSlideout = () => setDownloadReportSlideOut(false);

  const { data: tenantsResponse, error: tenantsError } = useSWR<
    ITenantPaginatedOutputResponse | undefined
  >(
    makeUrlWithParams(
      endpoints.v1_storefronts_id_tenants_id_tenants(
        storefront_id,
        user.tenant_id
      ),
      {
        offset: offset,
        limit: perPage,
        q: debouncedSearchQuery,
        // Don't let sort_by or order_by be empty strings(t).
        sort_by: sortingRules.sortBy || null,
        order_by: sortingRules.orderBy || "asc",
        tenant_type: "Buyer",
      }
    )
  );

  const handleSort = async (rules: SortingRule<object>[]) => {
    if (rules.length > 0) {
      const [rule, ...rest] = rules;
      const newRule = {
        id: rule.id === "location" ? "city" : rule.id,
        desc: rule.desc,
      };
      return defaultHandleSort(
        [newRule, ...rest],
        sortingRules,
        setSortingRules,
        setTableData
      );
    }
    return defaultHandleSort(
      rules,
      sortingRules,
      setSortingRules,
      setTableData
    );
  };

  useEffect(() => {
    if (tenantsResponse) {
      const { data: tenants, pagination } = tenantsResponse;

      setTableData(
        tenants.map((tenant: Tenant) => {
          const hqAddress = tenant.addresses.find(
            (address) => address.type === "Headquarters Address"
          );
          return {
            name: tenant.name,
            modified_at: getRelativeTime(tenant.modified_at),
            location: hqAddress ? formatAddressCityState(hqAddress) : "--",
            external_id: tenant.external_identity?.external_id || "--",
            id: tenant.id,
          };
        })
      );
      setPagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    }
  }, [tenantsResponse, setTableData, perPage]);

  const isLoading = !tenantsResponse && !tenantsError;

  const changePerPage = (perPage: number) => {
    setPerPage(perPage);
    if (perPage > offset) {
      setOffset(0);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const handlePageClick = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const handleRowClick = (e: React.MouseEvent): void => {
    history.push(
      makeUrlWithParams(`${adminPath}/tenants/${e.currentTarget.id}`, {
        q: query.q,
        offset,
        perPage,
      })
    );
  };

  useEffect(() => {
    // This useEffect handles keeping the query in sync with the URL.
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  useEffect(() => {
    setQuery({ offset, perPage });
  }, [offset, perPage, setQuery]);

  const columns = useMemo<ColumnDef<TenantsTable>[]>(
    () => [
      {
        accessorKey: "external_id",
        header: localStrings(t).externalID,
      },
      {
        accessorKey: "name",
        header: localStrings(t).companyName,
      },
      {
        accessorKey: "modified_at",
        header: localStrings(t).lastActivity,
        id: "modified_at",
        cell: (cell) => cell.renderValue(),
      },
      {
        accessorKey: "location",
        header: localStrings(t).location,
        align: "right",
      },
      // TODO: these are commented out until the API supports it.
      // { accessorKey: "status", header: strings(t).status, Cell: statusCellFn },
    ],
    [t]
  );

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>{localStrings(t).pageTitle}</PageTitle>
        <SearchBar
          maxWidth={"350px"}
          query={searchQuery}
          placeHolder={localStrings(t).searchBarPlaceholder}
          handleChange={handleSearch}
          handleClearInput={handleClearSearch}
        />
        {roleIsSomeKindOfSeller && (
          <HorizontalButtonsContainer
            style={{ gap: "8px", margin: "0 0 8px auto" }}
          >
            <SecondaryButtonMedium
              onClick={() => setDownloadReportSlideOut(true)}
            >
              {t("Download Report")}
            </SecondaryButtonMedium>
            {edition === "enterprise" && roleIsAgilisAdmin && (
              <PrimaryButtonMedium
                type="button"
                onClick={() => {
                  history.push(`${adminPath}/tenants/bulk-price-tiers`);
                }}
              >
                {t("Bulk Upload Price Tiers")}
              </PrimaryButtonMedium>
            )}
            <PrimaryButtonWithPlusIcon onClick={openModal} data-alignright>
              {localStrings(t).createNew}
            </PrimaryButtonWithPlusIcon>
          </HorizontalButtonsContainer>
        )}
        <Modal closeModal={closeModal} show={showModal}>
          <CreateNewTenantFormContainer
            sellerTenantId={user.tenant_id}
            type="Buyer"
            onSuccess={(tenantID) => {
              history.push(`${adminPath}/tenants/${tenantID}`);
              closeModal();
            }}
          />
        </Modal>
      </PageHeader>
      <CustomerDistributorDropdownContainer>
        <DropDown
          items={perPageItems}
          activeItem={perPage}
          showIcon
          hideBorder
          textLeft={t("items") + ":"}
          textRight={localStrings(t).perPage}
          direction={"left"}
          className={"per_Page"}
          clickHandler={changePerPage}
        />
      </CustomerDistributorDropdownContainer>
      <ContentWrapper>
        <Table
          columns={columns}
          isLoading={isLoading}
          error={tenantsError}
          data={tableData}
          rowClick={handleRowClick}
          handleSort={handleSort}
        />
        <Pagination
          pagination={pagination}
          offset={offset}
          handlePageClick={handlePageClick}
        />
        <SlideOut
          closeFlyout={closeDownloadReportSlideout}
          show={downloadReportSlideOut}
        >
          <DownloadReport
            closeSlideout={closeDownloadReportSlideout}
            transactionType={"customer"}
          />
        </SlideOut>
      </ContentWrapper>
    </PageWrapper>
  );
};
